import React, { useEffect, useState } from "react";
import { Input } from "@src/components/atoms/input/input";

import styles from "./tile-form.module.scss";
import {
  VALID_FREQUENCIES,
  VALID_GRID_AREAS,
} from "@src/cloud-connect/blueprint/dashboards/entities/tile";
import { VALID_TIME_UNITS } from "@src/cloud-connect/blueprint/dashboards/entities/time-period";
import { CheckBoxTextWrapper } from "@src/components/atoms/check-box-text-wrapper/check-box-text-wrapper";
import { useI18N } from "@src/language/language-context";

interface TileFormPropTypes {
  tile: {
    id: string;
    title: string;
    position: string;
    grid_area: string;
    time_period_amount: string;
    time_period_time_unit: string;
    use_aggregated_for_single_node: boolean;
  };
  createCallback: (tile) => void;
  cancelCallback: () => void;
}

const cssClassName = "tile-form";

export const TileForm = ({
  tile,
  createCallback,
  cancelCallback,
}: TileFormPropTypes) => {
  const i18n = useI18N();
  const texts = {
    id: i18n("modules.tileForm.id"),
    title: i18n("modules.tileForm.title"),
    chooseTileTitle: i18n("modules.tileForm.chooseTileTitle"),
    position: i18n("modules.tileForm.position"),
    chooseTilePosition: i18n("modules.tileForm.chooseTilePosition"),
    grid_area: i18n("modules.tileForm.grid_area"),
    chooseTileGridArea: i18n("modules.tileForm.chooseTileGridArea"),
    frequency: i18n("modules.tileForm.frequency"),
    time_period_amount: i18n("modules.tileForm.time_period_amount"),
    chooseTileTimePeriodAmount: i18n(
      "modules.tileForm.chooseTileTimePeriodAmount",
    ),
    time_period_time_unit: i18n("modules.tileForm.time_period_time_unit"),
    chooseTileTimePeriodTimeUnit: i18n(
      "modules.tileForm.chooseTileTimePeriodTimeUnit",
    ),
    create: i18n("modules.tileForm.create"),
    update: i18n("modules.tileForm.update"),
    cancel: i18n("modules.tileForm.cancel"),
    checkbox: i18n("modules.tileForm.checkbox"),
    gridAreaLabels: {
      quarter: i18n("modules.tileForm.gridAreaLabels.quarter"),
      third: i18n("modules.tileForm.gridAreaLabels.third"),
      half: i18n("modules.tileForm.gridAreaLabels.half"),
      full: i18n("modules.tileForm.gridAreaLabels.full"),
    },
    frequencyLabels: {
      quarterhourly: i18n("modules.tileForm.frequencyLabels.quarterhourly"),
      hourly: i18n("modules.tileForm.frequencyLabels.hourly"),
      daily: i18n("modules.tileForm.frequencyLabels.daily"),
      weekly: i18n("modules.tileForm.frequencyLabels.weekly"),
      monthly: i18n("modules.tileForm.frequencyLabels.monthly"),
      yearly: i18n("modules.tileForm.frequencyLabels.yearly"),
    },
    timePeriodLabels: {
      seconds: i18n("modules.tileForm.timePeriodLabels.seconds"),
      minutes: i18n("modules.tileForm.timePeriodLabels.minutes"),
      hours: i18n("modules.tileForm.timePeriodLabels.hours"),
      days: i18n("modules.tileForm.timePeriodLabels.days"),
      weeks: i18n("modules.tileForm.timePeriodLabels.weeks"),
    },
  };

  const [localTile, setLocalTile] = useState(undefined);

  const createTile = () => {
    createCallback(localTile);
  };

  const cancelCreateTile = () => {
    cancelCallback();
  };

  const onTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalTile({ ...localTile, title: e.target.value });
  };

  const onPositionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalTile({ ...localTile, position: Number(e.target.value) });
  };

  const onGridAreaChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setLocalTile({ ...localTile, grid_area: e.target.value });
  };

  const onFrequencyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setLocalTile({ ...localTile, frequency: e.target.value });
  };

  const onTimePeriodAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalTile({ ...localTile, time_period_amount: Number(e.target.value) });
  };

  const onTimePeriodTimeUnitChange = (
    e: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    setLocalTile({ ...localTile, time_period_time_unit: e.target.value });
  };

  const onFetchAggregatedForSingleNodeChange = () => {
    setLocalTile({
      ...localTile,
      use_aggregated_for_single_node: !localTile.use_aggregated_for_single_node,
    });
  };

  useEffect(() => {
    setLocalTile(tile);
  }, [tile]);

  return (
    <>
      {localTile && (
        <div className={styles[cssClassName]}>
          <form>
            <Input
              labelText={texts.id}
              inputPlaceHolder={""}
              inputValue={localTile.id}
              onChangeFunc={(e) => {
                console.log(e);
              }}
            />

            <Input
              labelText={texts.title}
              inputPlaceHolder={texts.chooseTileTitle}
              inputValue={localTile.title}
              onChangeFunc={(e) => onTitleChange(e)}
            />

            <Input
              labelText={texts.position}
              inputPlaceHolder={texts.chooseTilePosition}
              inputValue={localTile.position}
              onChangeFunc={(e) => onPositionChange(e)}
              type={"number"}
            />

            <div>
              <label className={styles[`${cssClassName}__label`]}>
                {texts.grid_area}
                <select
                  value={localTile.grid_area}
                  onChange={(e) => onGridAreaChange(e)}
                  className={styles[`${cssClassName}__dropdown`]}
                >
                  {VALID_GRID_AREAS.map((gridArea) => (
                    <option key={gridArea} value={gridArea}>
                      {texts.gridAreaLabels[gridArea]}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <CheckBoxTextWrapper
              text={texts.checkbox}
              checkboxState={localTile.use_aggregated_for_single_node}
              handleCheckBoxClick={onFetchAggregatedForSingleNodeChange}
            />

            <div>
              <label className={styles[`${cssClassName}__label`]}>
                {texts.frequency}
                <select
                  value={localTile.frequency}
                  onChange={(e) => onFrequencyChange(e)}
                  className={styles[`${cssClassName}__dropdown`]}
                >
                  {VALID_FREQUENCIES.map((frequency) => (
                    <option key={frequency} value={frequency}>
                      {texts.frequencyLabels[frequency]}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <Input
              labelText={texts.time_period_amount}
              inputPlaceHolder={texts.chooseTileTimePeriodAmount}
              inputValue={localTile.time_period_amount}
              onChangeFunc={(e) => onTimePeriodAmountChange(e)}
              type={"number"}
            />

            <div>
              <label className={styles[`${cssClassName}__label`]}>
                {texts.time_period_time_unit}
                <select
                  value={localTile.time_period_time_unit}
                  onChange={(e) => onTimePeriodTimeUnitChange(e)}
                  className={styles[`${cssClassName}__dropdown`]}
                >
                  {VALID_TIME_UNITS.map((timeUnit) => (
                    <option key={timeUnit} value={timeUnit}>
                      {texts.timePeriodLabels[timeUnit]}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </form>
          <div className={styles[cssClassName]}>
            <button onClick={createTile}>{texts.create}</button>
            <button onClick={cancelCreateTile}>{texts.cancel}</button>
          </div>
        </div>
      )}
    </>
  );
};
